import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  CircularProgress,
  Chip,
  Button,
  TablePagination,
  Snackbar
} from '@mui/material';
import {
  Search,
} from '@mui/icons-material';
import {
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line
} from 'recharts';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const ContractEmployeeReport = ({ data = [], period = 'yearly' }) => {
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Fetch contract employees data
  useEffect(() => {
    if (data && data.length > 0) {
      setEmployees(data);
      setFilteredEmployees(data);
      setLoading(false);
    } else {
      fetchContractEmployees();
    }
  }, [data]);

  const fetchContractEmployees = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }
    
      setLoading(true);
      setError(null);

      const response = await axios.get(`${API_URL}/api/employee-report/summary?period=${period}`, {
        timeout: 10000,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data.status === "success" && response.data.data) {
        const employeesData = response.data.data.contractEmployees || [];
        setEmployees(employeesData);
        setFilteredEmployees(employeesData);
        setSuccessMessage(`Successfully loaded ${employeesData.length} contract employees`);
      } else {
        throw new Error(response.data.message || 'Failed to load contract employees');
      }
    } catch (error) {
      //console.error('Error fetching contract employees:', error);
      setError(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get contract status
  const getContractStatus = (endDateStr) => {
    if (!endDateStr) return null; // Don't count employees with no end date
    
    const endDate = new Date(endDateStr);
    const today = new Date();
    endDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    
    const diffTime = endDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'expired';
    if (diffDays <= 30) return 'expiring_soon';
    return 'active';
  };

  // Filter employees to show only expired ones and apply search/department filters
  const filteredData = useMemo(() => {
    let filtered = employees.filter(employee => {
      // First filter: Only expired employees
      const employeeStatus = getContractStatus(employee.contractEndDate);
      if (employeeStatus !== 'expired') return false;

      // Then apply search filter
      const matchesSearch = !searchTerm || 
        (employee.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
         employee.c_employee_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
         employee.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
         employee.department?.toLowerCase().includes(searchTerm.toLowerCase()) ||
         employee.roleType?.toLowerCase().includes(searchTerm.toLowerCase()) ||
         employee.companyName?.toLowerCase().includes(searchTerm.toLowerCase()));

      // Apply department filter
      const matchesDepartment = departmentFilter === 'all' || 
        employee.department?.toLowerCase() === departmentFilter.toLowerCase();

      return matchesSearch && matchesDepartment;
    });

    return filtered;
  }, [employees, searchTerm, departmentFilter]);

  // Calculate analytics data
  const analyticsData = useMemo(() => {
    const statusCounts = {
      active: 0,
      expired: 0,
      expiring_soon: 0
    };

    const monthlyData = {};

    employees.forEach(emp => {
      // Only count employees with valid end dates
      const status = getContractStatus(emp.contractEndDate);
      if (status) {
        statusCounts[status]++;
      }

      // Monthly contract starts (only count if there's a start date)
      if (emp.contractStartDate) {
        const date = new Date(emp.contractStartDate);
        const month = date.toLocaleString('default', { month: 'short' });
        const year = date.getFullYear();
        const monthYear = `${month} ${year}`;
        monthlyData[monthYear] = (monthlyData[monthYear] || 0) + 1;
      }
    });

    // Filter out any status with zero count to prevent overlapping in pie chart
    const statusData = [
      { name: 'Active', value: statusCounts.active, color: '#4caf50' },
      { name: 'Expiring Soon', value: statusCounts.expiring_soon, color: '#ff9800' },
      { name: 'Expired', value: statusCounts.expired, color: '#f44336' }
    ].filter(item => item.value > 0); // Only show statuses with values > 0

    // Sort monthly data chronologically
    const sortedMonthlyData = Object.entries(monthlyData)
      .map(([month, count]) => ({ month, count }))
      .sort((a, b) => {
        const dateA = new Date(a.month);
        const dateB = new Date(b.month);
        return dateA - dateB;
      });

    return {
      statusData,
      monthlyData: sortedMonthlyData
    };
  }, [employees]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return "Invalid Date";
    }
  };

  const getStatusChip = (endDateStr) => {
    const status = getContractStatus(endDateStr);
    const statusConfig = {
      active: { label: 'Active', color: 'success' },
      expiring_soon: { label: 'Expiring Soon', color: 'warning' },
      expired: { label: 'Expired', color: 'error' }
    };

    const config = statusConfig[status] || { label: 'No End Date', color: 'default' };
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setDepartmentFilter('all');
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{ backgroundColor: 'white', padding: '10px', border: '1px solid #ccc', borderRadius: '5px' }}>
          <p style={{ margin: 0 }}>{`${label}: ${payload[0].value}`}</p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress size={50} />
        <Typography sx={{ ml: 2 }}>Loading contract employees...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: '1400px', margin: 'auto', padding: 3 }}>
     
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Contract Status Pie Chart */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: 400 }}>
            <Typography variant="h6" gutterBottom>
              Contract Status Distribution
            </Typography>
            {analyticsData.statusData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={analyticsData.statusData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name }) => `${name}`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                    isAnimationActive={false}
                  >
                    {analyticsData.statusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                <Typography color="text.secondary">No status data available</Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Contract Starts by Month */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: 400 }}>
            <Typography variant="h6" gutterBottom>
              Contract Starts by Month
            </Typography>
            {analyticsData.monthlyData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={analyticsData.monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Line type="monotone" dataKey="count" stroke="#ff9800" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                <Typography color="text.secondary">No monthly data available</Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Summary */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="body1" fontWeight="500">
          Showing {filteredData.length} expired contract employees
        </Typography>
      </Box>

      {/* Employee Table */}
      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table>
          <TableHead sx={{ bgcolor: '#f5f5f5' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Employee ID</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Department</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Company</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Start Date</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>End Date</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Projects</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    No expired contract employees found matching your criteria
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredData
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((employee, index) => (
                  <TableRow key={employee.c_employee_id || index} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="500">
                        {employee.c_employee_id || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="500">
                          {employee.fullName || 'N/A'}
                        </Typography>
                        {employee.email && (
                          <Typography variant="caption" color="text.secondary">
                            {employee.email}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{employee.department || 'N/A'}</TableCell>
                    <TableCell>{employee.roleType || 'N/A'}</TableCell>
                    <TableCell>{employee.companyName || 'N/A'}</TableCell>
                    <TableCell>{formatDate(employee.contractStartDate)}</TableCell>
                    <TableCell>{formatDate(employee.contractEndDate)}</TableCell>
                    <TableCell>{getStatusChip(employee.contractEndDate)}</TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {employee.projects && employee.projects.length > 0 
                          ? employee.projects.map(p => p.name).join(', ')
                          : 'No projects'
                        }
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {filteredData.length > 0 && (
        <TablePagination
          component="div"
          count={filteredData.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      )}
    </Box>
  );
};

export default ContractEmployeeReport;